import { useCallback, useState } from 'react';
import * as gtag from '@lib/utils/gtag';
import { getFormattedPrice, getProductPromotion } from '@lib/utils';
import { PriceT } from '@lib/utils/getFormattedPrice/types';
import { DataLayerParametersI } from '@providers/DataLayerProvider';
import { getMonthlyPriceCalculated, getOriginalPrice } from '@lib/utils';
import usePostcodeContext from './usePostcodeContext';
import { GA4ItemI } from '@lib/utils/gtag';
import useBasketContext from '@hooks/useBasketContext';
import { CardPackageT } from '@components/Organisms/ChoosePackage/types';
import useBasketContents from '@hooks/useBasketContents';

interface GAEventI {
  action: string;
  parameters?: any;
  category?: string;
  label?: string;
  value?: string;
}

interface GAEventReturn {
  send: (event: GAEventI, useAPI?: boolean) => Promise<any>;
  parameters: DataLayerParametersI;
}

const getTotalValue = (items: GA4ItemI[]) => {
  return items.reduce((acc, item) => acc + (item.price || 0), 0);
};

const PackageTypeMap: {
  [key: string]: string;
} = {
  HWRK: 'Home Worker',
  RES: 'Home',
  BUS: 'Business',
  MSM: 'Multi-dwelling',
};

export interface PurchaseParamsI {
  transaction_id: string;
  currency: 'GBP';
  value: number;
  items: gtag.GA4ItemI[];
  OrderID: string;
  OrderValue: string;
  ProductType: string;
  Extras: string;
  OrderStatus: string;
  OrderLocation: string;
  CommissionGroup: string;
  PromoCodes: string;
  AWC?: string;
}

export interface ViewItemParamsI {
  currency: 'GBP';
  value: PriceT;
  items: gtag.GA4ItemI[];
}
export interface ViewItemListParamsT {
  currency?: 'GBP';
  items: {
    item_id: string;
    item_name: string;
  }[];
}

function useGAEvent(): GAEventReturn {
  const { getBasketItemProduct, promoCodes, monthlyPriceCalculated, originalPrice } =
    useBasketContext();
  const { postcodeItem } = usePostcodeContext();
  const [parameters, setParameters] = useState<DataLayerParametersI>({} as DataLayerParametersI);

  const basketItem = getBasketItemProduct();
  const basketContents = useBasketContents({ basketItem });

  const formatBasketItems = useCallback(() => {
    const { allProducts, addonsMonthlyFormattedPrices, addonsOriginalFormattedPrices } =
      basketContents;

    return allProducts
      .filter((product) => product)
      .map((product, index) => {
        // For main product
        if (index === 0) {
          const monthlyPrice = +(monthlyPriceCalculated ?? 0);
          const origPrice = +(originalPrice ?? 0);

          const itemPrice = +getFormattedPrice(monthlyPrice, true);
          const itemDiscount =
            origPrice > monthlyPrice ? +getFormattedPrice(origPrice - monthlyPrice, true) : 0;

          return {
            item_id: product?.id_product || 'unknown',
            item_name: product?.name || '',
            item_list_name: `${PackageTypeMap[product?.address_type || '']} Broadband`,
            item_category: PackageTypeMap[product?.address_type || ''],
            item_category2:
              product?.product_type === 'ADDON' ? undefined : `${product?.months} months`,
            price: itemPrice,
            discount: itemDiscount,
            coupon: promoCodes?.map((promo) => promo?.code).join(',') || '',
          };
        }

        // For addons (index > 0)
        const itemPrice = +addonsMonthlyFormattedPrices[index - 1];
        let itemDiscount = 0;

        const addonOriginalPrice = addonsOriginalFormattedPrices[index - 1];
        if (addonOriginalPrice && +addonOriginalPrice !== itemPrice) {
          itemDiscount = +addonOriginalPrice - itemPrice;
        }

        return {
          item_id: product?.id_product || 'unknown',
          item_name: product?.name || '',
          item_list_name: 'Extras',
          item_category: 'Extra',
          price: itemPrice,
          discount: itemDiscount,
          coupon: promoCodes?.map((promo) => promo?.code).join(',') || '',
        };
      });
  }, [basketContents, monthlyPriceCalculated, originalPrice, promoCodes]);

  const formatParameters = useCallback(
    ({ action, parameters = {} }: GAEventI) => {
      const ActionParametersMap: { [key: string]: any } = {
        add_to_cart: () => {
          if (parameters.selected_product) {
            const mainProduct = {
              item_id: parameters.selected_product.id_product || 'unknown',
              item_name: parameters.selected_product.name || '',
              item_list_name: `${
                PackageTypeMap[parameters.selected_product.address_type || '']
              } Broadband`,
              item_category: PackageTypeMap[parameters.selected_product.address_type || ''],
              item_category2: `${parameters.selected_product.months} months`,
              price: +getFormattedPrice(parameters.selected_product.monthlyPriceCalculated, true),
              discount: +getFormattedPrice(
                parameters.selected_product.originalPrice -
                  parameters.selected_product.monthlyPriceCalculated,
                true,
              ),
            };

            const extras =
              parameters.extras?.map((extra) => ({
                item_id: extra.id_product || 'unknown',
                item_name: extra.name || 'unknown',
                item_list_name: 'Extras',
                item_category: 'Extra',
                price: +getFormattedPrice(extra.monthly_price || 0, true),
                discount: 0,
              })) || [];

            const items = [mainProduct, ...extras];

            return {
              currency: 'GBP',
              items,
              value: getTotalValue(items),
            };
          }

          const items = formatBasketItems();
          return {
            currency: 'GBP',
            items,
            value: getTotalValue(items),
          };
        },

        add_payment_info: () => ({
          currency: 'GBP',
          items: formatBasketItems(),
          value: getTotalValue(formatBasketItems()),
          ...parameters,
        }),

        purchase: () => ({
          currency: 'GBP',
          items: formatBasketItems(),
          value: getTotalValue(formatBasketItems()),
          ...parameters,
        }),

        add_extras: () => {
          const basketItems = formatBasketItems();
          const { add_extra, monthlyPriceCalculated, originalPrice } = parameters;

          const newItem = {
            item_id: add_extra?.id_product || 'unknown',
            item_name: add_extra?.name || 'unknown',
            item_list_name: 'Extras',
            item_category: 'Extra',
            price: +getFormattedPrice(monthlyPriceCalculated || 0, true),
            discount: originalPrice
              ? +getFormattedPrice(originalPrice - monthlyPriceCalculated, true)
              : 0,
          };

          const items = [...basketItems.filter((item) => item.item_category === 'Extra'), newItem];

          return {
            currency: 'GBP',
            value: getTotalValue(items),
            items,
          };
        },

        remove_extras: () => {
          const basketItems = formatBasketItems();
          const { extraId } = parameters;

          const items = basketItems.filter((item) => item.item_id !== extraId);

          return {
            currency: 'GBP',
            value: getTotalValue(items),
            items,
          };
        },

        view_item_list: () => {
          const { viewed_items: products, promotions } = parameters;

          if (!products) {
            return {
              currency: 'GBP',
              items: [],
            };
          }

          const items = products.map((product: CardPackageT) => {
            const promos = getProductPromotion({
              id_product: product.id_product,
              coverage: postcodeItem?.coverage,
              promotions,
            });

            const deductablePromo = Array.isArray(promos)
              ? promos.find(
                  ({ promotion_type }) =>
                    promotion_type === 'RECURAMTOFF' || promotion_type === 'RECUR%OFF',
                )
              : promos;

            const originalPrice = getOriginalPrice({
              display_original_price: product.display_original_price,
              monthly_price: product.monthly_price,
              promo: deductablePromo,
              formatPrice: true,
            });

            const monthlyPriceCalculated = getMonthlyPriceCalculated({
              monthly_price: product.monthly_price,
              promo: deductablePromo,
              formatPrice: true,
            });

            const value = +getFormattedPrice(monthlyPriceCalculated, true);

            return {
              item_id: product.id_product,
              item_name: product.name,
              item_list_name: `${PackageTypeMap[product.address_type || '']} Broadband`,
              item_category: PackageTypeMap[product.address_type || ''],
              item_category2: `${product.months} months`,
              price: value,
              discount: +getFormattedPrice(
                Number(originalPrice) - Number(monthlyPriceCalculated),
                true,
              ),
              coupon: Array.isArray(promos) ? promos.map((promo) => promo?.code).join(',') : '',
            };
          });

          return {
            currency: 'GBP',
            items,
          };
        },

        view_item: () => ({
          currency: 'GBP',
          items: parameters.items || [],
        }),

        add_shipping_info: () => {
          const basketItems = formatBasketItems();

          return {
            currency: 'GBP',
            items: basketItems,
            coupon: parameters.coupon,
          };
        },

        select_promotion: () => {
          const { promotion_name } = parameters;
          const basketItems = formatBasketItems();

          return {
            promotion_name: promotion_name || '',
            items: basketItems.map((item) => ({
              ...item,
              coupon: item.coupon ? `${item.coupon},${promotion_name}` : promotion_name,
            })),
          };
        },
      };

      const formatter = ActionParametersMap[action];
      return formatter ? formatter() : parameters;
    },
    [formatBasketItems, postcodeItem?.coverage],
  );

  const send = useCallback(
    (event: GAEventI) => {
      const formattedParameters = formatParameters(event);
      setParameters(formattedParameters);

      gtag.event({
        ...event,
        parameters: {
          ...formattedParameters,
          debug_mode: typeof window !== 'undefined' ? localStorage.getItem('debug_mode') : null,
        },
      });

      return new Promise((resolve) => {
        resolve(true);
      });
    },
    [formatParameters],
  );

  return {
    send,
    parameters,
  };
}

export default useGAEvent;
